import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getBlogPosts } from '../data/blogPosts';

export default function Blog() {
  const { language, t } = useLanguage();
  const posts = getBlogPosts(language);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900 to-slate-950 z-10" />
        <div 
            className="absolute inset-0 opacity-20"
            style={{
            backgroundImage: 'radial-gradient(circle at top, rgba(34,211,238,0.12), transparent 40%), linear-gradient(to bottom, rgba(15,23,42,0.65), rgba(2,6,23,0.98))',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            }}
        />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-6">
                <BookOpen className="w-4 h-4" />
                ProcessSurge Blog
            </div>
            <h1 className="font-outfit text-5xl lg:text-7xl font-bold text-slate-100 tracking-tight mb-6">
              {language === 'pt' ? 'Insights de Automação' : 'Automation Insights'}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {language === 'pt' 
               ? 'Tutoriais técnicos, estratégias de retenção e bastidores da engenharia de dados.'
               : 'Technical tutorials, retention strategies, and data engineering behind the scenes.'}
            </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 lg:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-slate-800 rounded-3xl">
              <p className="text-slate-500 text-lg">{t.blog.emptyState}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/${language}/blog/${post.slug}`}
                  className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden hover:border-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-900/10 transition-all duration-300 group flex flex-col h-full"
                >
                  <div className="p-8 flex flex-col h-full">
                    <div className="flex items-center gap-2 text-cyan-500 text-sm mb-4 font-mono">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.published_at)}
                    </div>

                    <h2 className="font-outfit text-2xl font-bold text-slate-100 mb-4 group-hover:text-cyan-400 transition-colors leading-tight">
                      {post.title}
                    </h2>

                    <p className="text-slate-400 leading-relaxed mb-6 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-800">
                        <div className="flex gap-2">
                            {post.tags?.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-xs text-slate-500 bg-slate-950 px-2 py-1 rounded-md border border-slate-800">
                                #{tag}
                            </span>
                            ))}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500 group-hover:text-slate-950 transition-colors">
                             <ArrowRight className="w-4 h-4" />
                        </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
